import { useCallback, useMemo, useState } from "react"

import { quantileFromSorted } from "@/utils/quantile"

export const DISTRIBUTION_STAT_KEY = {
  MEAN: "mean",
  STD_DEV: "stdDev",
  MEDIAN: "median",
  P95: "p95",
} as const

export type DistributionStatKey =
  (typeof DISTRIBUTION_STAT_KEY)[keyof typeof DISTRIBUTION_STAT_KEY]

export interface DistributionStats {
  mean: number
  stdDev: number
  median: number
  p95: number
}

const STAT_LINE_COLORS: Record<DistributionStatKey, string> = {
  mean: "hsl(var(--heroui-primary))",
  stdDev: "hsl(var(--heroui-warning-600, 38 92% 35%))",
  median: "hsl(var(--heroui-success))",
  p95: "hsl(var(--heroui-danger))",
}

interface StatLineData {
  x: number
  stroke: string
  label: string
}

export function buildStatLines(
  distributionStats: DistributionStats,
  statsToShow: Set<DistributionStatKey>,
  valueToX: (value: number) => number,
): StatLineData[] {
  const lines: StatLineData[] = []
  if (statsToShow.has(DISTRIBUTION_STAT_KEY.MEAN)) {
    lines.push({
      x: valueToX(distributionStats.mean),
      stroke: STAT_LINE_COLORS.mean,
      label: "Mean",
    })
  }
  if (statsToShow.has(DISTRIBUTION_STAT_KEY.STD_DEV)) {
    lines.push({
      x: valueToX(distributionStats.mean + distributionStats.stdDev),
      stroke: STAT_LINE_COLORS.stdDev,
      label: "+1 σ",
    })
  }
  if (statsToShow.has(DISTRIBUTION_STAT_KEY.MEDIAN)) {
    lines.push({
      x: valueToX(distributionStats.median),
      stroke: STAT_LINE_COLORS.median,
      label: "Median",
    })
  }
  if (statsToShow.has(DISTRIBUTION_STAT_KEY.P95)) {
    lines.push({
      x: valueToX(distributionStats.p95),
      stroke: STAT_LINE_COLORS.p95,
      label: "P95",
    })
  }
  return lines
}

export function useStatsToggle(
  initialStats: DistributionStatKey[] = [
    DISTRIBUTION_STAT_KEY.MEAN,
    DISTRIBUTION_STAT_KEY.STD_DEV,
    DISTRIBUTION_STAT_KEY.MEDIAN,
    DISTRIBUTION_STAT_KEY.P95,
  ],
) {
  const [visibleStats, setVisibleStats] = useState<Set<DistributionStatKey>>(
    () => new Set(initialStats),
  )
  const [hoveredStat, setHoveredStat] = useState<DistributionStatKey | null>(
    null,
  )

  const handleToggleStat = useCallback((key: DistributionStatKey) => {
    setVisibleStats((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  const statsToShow = useMemo(
    () =>
      hoveredStat != null
        ? new Set([...visibleStats, hoveredStat])
        : visibleStats,
    [visibleStats, hoveredStat],
  )

  return {
    visibleStats,
    hoveredStat,
    setHoveredStat,
    handleToggleStat,
    statsToShow,
  }
}

/**
 * Compute distribution statistics from an array of numeric values.
 * Returns mean, sample standard deviation, median, and 95th percentile.
 */
export function computeDistributionStats(values: number[]): DistributionStats {
  if (values.length === 0) {
    return { mean: 0, stdDev: 0, median: 0, p95: 0 }
  }

  const sorted = [...values].sort((a, b) => a - b)
  const n = values.length
  const sum = values.reduce((a, b) => a + b, 0)
  const mean = sum / n

  const variance =
    n <= 1
      ? 0
      : values.reduce((acc, val) => acc + (val - mean) ** 2, 0) / (n - 1)
  const stdDev = Math.sqrt(variance)

  const median =
    n % 2 === 1
      ? sorted[Math.floor(n / 2)]
      : (sorted[n / 2 - 1] + sorted[n / 2]) / 2

  const p95Index = Math.min(n - 1, Math.ceil(n * 0.95) - 1)
  const p95 = sorted[Math.max(0, p95Index)]

  return { mean, stdDev, median, p95 }
}

// ─── Box plot statistics ──────────────────────────────────────────────────────

export interface QuartileStats {
  q1: number
  median: number
  q3: number
  whiskerLow: number
  whiskerHigh: number
  outliers: number[]
}

interface FullStats {
  count: number
  mean: number
  stdDev: number
  median: number
  p95: number
  min: number
  max: number
  q1: number
  q3: number
  whiskerLow: number
  whiskerHigh: number
  outliers: number[]
}

/**
 * Compute Tukey box plot statistics (Q1, median, Q3, whiskers, outliers).
 * Returns null for empty input.
 */
export function computeQuartileStats(values: number[]): QuartileStats | null {
  if (values.length === 0) {
    return null
  }
  const sorted = [...values].sort((a, b) => a - b)
  const q1 = quantileFromSorted(sorted, 0.25)
  const median = quantileFromSorted(sorted, 0.5)
  const q3 = quantileFromSorted(sorted, 0.75)
  const iqr = q3 - q1
  const whiskerLow = Math.max(sorted[0], q1 - 1.5 * iqr)
  const whiskerHigh = Math.min(sorted[sorted.length - 1], q3 + 1.5 * iqr)
  const outliers = sorted.filter((v) => v < whiskerLow || v > whiskerHigh)
  return { q1, median, q3, whiskerLow, whiskerHigh, outliers }
}

// ─── Kernel density estimation (for violin plots) ───────────────────────────

interface KdePoint {
  value: number
  density: number
}

/**
 * Gaussian kernel density estimation using Silverman's rule-of-thumb bandwidth.
 * Returns an array of {value, density} points sampled evenly across the data range.
 */
export function computeKde(values: number[], nPoints = 64): KdePoint[] {
  if (values.length === 0) {
    return []
  }
  const sorted = [...values].sort((a, b) => a - b)
  const n = sorted.length

  if (n === 1) {
    return [{ value: sorted[0], density: 1 }]
  }

  const mean = sorted.reduce((a, b) => a + b, 0) / n
  const variance = sorted.reduce((acc, v) => acc + (v - mean) ** 2, 0) / (n - 1)
  const stdDev = Math.sqrt(variance)

  const iqr =
    quantileFromSorted(sorted, 0.75) - quantileFromSorted(sorted, 0.25)
  // Silverman's rule: h = 0.9 * min(stdDev, IQR/1.34) * n^(-1/5)
  const spread = iqr > 0 ? Math.min(stdDev, iqr / 1.34) : stdDev
  const bandwidth = Math.max(
    0.9 * spread * n ** -0.2,
    (sorted[n - 1] - sorted[0]) * 1e-4 || 1e-6,
  )

  const lo = sorted[0] - 3 * bandwidth
  const hi = sorted[n - 1] + 3 * bandwidth
  const step = (hi - lo) / (nPoints - 1)
  const INV_SQRT_2PI = 1 / Math.sqrt(2 * Math.PI)

  const points: KdePoint[] = []
  for (let i = 0; i < nPoints; i++) {
    const x = lo + i * step
    let sum = 0
    for (let j = 0; j < n; j++) {
      const z = (x - sorted[j]) / bandwidth
      sum += INV_SQRT_2PI * Math.exp(-0.5 * z * z)
    }
    points.push({ value: x, density: sum / (n * bandwidth) })
  }
  return points
}

/**
 * Compute all distribution and box plot statistics in a single sorted pass.
 * Combines computeDistributionStats + computeQuartileStats without double-sorting.
 * Returns null for empty input.
 */
export function computeFullStats(values: number[]): FullStats | null {
  if (values.length === 0) {
    return null
  }
  const sorted = [...values].sort((a, b) => a - b)
  const n = sorted.length

  const mean = sorted.reduce((a, b) => a + b, 0) / n
  const variance =
    n <= 1 ? 0 : sorted.reduce((acc, v) => acc + (v - mean) ** 2, 0) / (n - 1)
  const stdDev = Math.sqrt(variance)

  const q1 = quantileFromSorted(sorted, 0.25)
  const median = quantileFromSorted(sorted, 0.5)
  const q3 = quantileFromSorted(sorted, 0.75)

  const p95Index = Math.min(n - 1, Math.ceil(n * 0.95) - 1)
  const p95 = sorted[Math.max(0, p95Index)]

  const iqr = q3 - q1
  const whiskerLow = Math.max(sorted[0], q1 - 1.5 * iqr)
  const whiskerHigh = Math.min(sorted[n - 1], q3 + 1.5 * iqr)
  const outliers = sorted.filter((v) => v < whiskerLow || v > whiskerHigh)

  return {
    count: n,
    mean,
    stdDev,
    median,
    p95,
    min: sorted[0],
    max: sorted[n - 1],
    q1,
    q3,
    whiskerLow,
    whiskerHigh,
    outliers,
  }
}
